<template>
  <!-- eslint-disable-next-line vue/no-v-model-argument -->
  <Dialog v-model:open="openProxy">
    <DialogContent data-test-id="image-lightbox" :show-close-button="false" class="image-lightbox-content">
      <DialogTitle class="sr-only">Image preview</DialogTitle>
      <img
        v-if="current"
        data-test-id="image-lightbox-current"
        class="image-lightbox-img"
        :src="current.signedUrl"
        :alt="current.name"
      />
      <button
        v-if="images.length > 1"
        type="button"
        data-test-id="image-lightbox-prev"
        class="image-lightbox-btn image-lightbox-btn--prev"
        :disabled="indexProxy === 0"
        @click="prev"
        aria-label="Previous image"
      >
        ‹
      </button>
      <button
        v-if="images.length > 1"
        type="button"
        data-test-id="image-lightbox-next"
        class="image-lightbox-btn image-lightbox-btn--next"
        :disabled="indexProxy === images.length - 1"
        @click="next"
        aria-label="Next image"
      >
        ›
      </button>
      <button
        type="button"
        data-test-id="image-lightbox-close"
        class="image-lightbox-btn image-lightbox-btn--close"
        @click="close"
        aria-label="Close"
      >
        ✕
      </button>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'

type ImageItemType = { id: string; name: string; ext: string; signedUrl?: string }

const props = defineProps<{
  open: boolean
  index: number
  images: ImageItemType[]
}>()

const emits = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'update:index', value: number): void
}>()

const openProxy = computed({
  get: () => props.open,
  set: (v: boolean) => emits('update:open', v),
})

const indexProxy = computed({
  get: () => props.index,
  set: (v: number) => emits('update:index', v),
})

const current = computed(() => props.images[indexProxy.value])

function prev() {
  if (indexProxy.value > 0) emits('update:index', indexProxy.value - 1)
}
function next() {
  if (indexProxy.value < props.images.length - 1) emits('update:index', indexProxy.value + 1)
}
function close() {
  emits('update:open', false)
}

function onKeydown(e: KeyboardEvent) {
  if (!props.open) return
  if (e.key === 'ArrowRight') {
    e.preventDefault()
    next()
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault()
    prev()
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener('keydown', onKeydown)
    } else {
      document.removeEventListener('keydown', onKeydown)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style lang="scss">
// Fallback if Tailwind's bg-black/80 on DialogOverlay doesn't render in this project.
[data-slot='dialog-overlay'] {
  background-color: rgba(0, 0, 0, 0.8);
}

.image-lightbox-content {
  max-width: 90vw !important;
  width: 90vw !important;
  min-width: 320px !important;
  min-height: 240px !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
}

.image-lightbox-img {
  max-width: 90vw;
  max-height: 85vh;
  min-width: 100px;
  min-height: 100px;
  display: block;
  border-radius: 8px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.05);
}

.image-lightbox-btn {
  position: absolute;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.75);
  }

  &--prev {
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
  }
  &--next {
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
  }
  &--close {
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    font-size: 20px;
  }
}
</style>
